import { rows, DataGrid, Dropzone, DragAndDropInfo } from "colbi_web_ui/lib/components";
import { parseFilter } from "colbi_web_ui/lib/utils";
import FileDetail from "../controllers/Repository/FileDetail";
import { getConfigVariable } from "../config/config";
import { controllerQueries } from "../controllers/utils/utils";
import { hasModule } from "../utils/utils";
import Repository from "../controllers/Repository/Repository";
import RepositoryNewLogic from "../controllers/Repository/RepositoryNewLogic";
import RepositoryTabs from "../components/RepositoryTabs/RepositoryTabs";
import RepositoryToolbar from "../components/RepositoryToolbar/RepositoryToolbar";
import { StackLayout } from "../layouts";
import UploadFormHeaderButtons from "../components/RepositoryUploadButton/UploadFormHeaderButtons";

const RepositoryController = (props) => {
    const { modules } = props;
    const Controller = hasModule(modules, "new_navigation") ? RepositoryNewLogic : Repository;
    return <Controller {...props} />;
};

RepositoryController.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

const buildDatagrid = (dragAndDropActive) => {
    const datagridProps = {
        id: "results",
        card: rows.CellCard,
        checkbox: true,
        items: "query(0).list",
        header: "query(0).header",
        footer: "query(0).footer",
        metadata: "query(0).metadata",
        page: "param(page)",
        status: "status(0)",
        sort: "param(sort)",
        pageSize: "param(pageSize)",
        pageDefault: 10,
        filter: "param(filter)",
        availableFilters: "query(0).filters",
        wrapper: "wrapper",
        showCheckbox: "query(0).showCheckbox",
        total: "query(0).metadata.total",
        projectModules: "query(2)",
        toolbar: RepositoryToolbar,
    };
    return dragAndDropActive
        ? [
              {
                  component: DragAndDropInfo,
                  props: {
                      csvDelimiter: "query(3)",
                  },
              },
              {
                  component: Dropzone,
                  props: {
                      Children: DataGrid,
                      childrenProps: datagridProps,
                      modifiers: ["--root-margin-top-small", "--row-style-alternate"],
                  },
              },
          ]
        : [
              {
                  component: DataGrid,
                  props: datagridProps,
                  modifiers: ["--root-margin-top-small", "--row-style-alternate"],
              },
          ];
};

const queries = (args = {}) => {
    // Extract viewMode from args (comes from URL query parameter)
    const viewMode = args.viewMode;
    // Extract importId - if it's "full", don't send it to the backend
    // Use args.importId from URL params, NOT args.id (which is for file detail)
    const importId = args.importId && args.importId !== "full" ? args.importId : undefined;

    return [
        {
            resource: args.section,
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear || "",
                fileType: args.fileType || "",
                ...(viewMode && { viewMode }), // Add viewMode if it exists
                ...(importId && { importId }), // Add importId if it exists and is not "full"
                sort: args.sort
                    ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                    : undefined,
                filter: args.filter
                    ? { type: "[FilterInput!]", value: parseFilter(args.filter) }
                    : undefined,
                page: parseInt(args.page, 10) || 0,
                pageSize: parseInt(
                    args.pageSize || getConfigVariable("REACT_APP_DEFAULT_PAGE_SIZE"),
                ),
            },
            body: `__typename, items{...${args.section}}, total, hasMore,filters(projectId:$projectId, fiscalYear:$fiscalYear){ prop, name, type, list, disableOperators, mandatory}`,
            downloadable: true,
            forceRefresh: true,
            isDatagridReq: true,
        },
        {
            resource: "show_terms_and_conditions",
            args: {
                projectId: args.projectId,
            },
            forceRefresh: true,
        },
        {
            resource: "project_modules",
            args: {
                projectId: args.projectId,
            },
            body: "modules",
        },
        {
            resource: "csv_delimiter",
            args: {
                projectId: args.projectId,
            },
            forceRefresh: true,
        },
        {
            resource: "data_files_types_present",
            args: {
                projectId: args.projectId,
            },
            body: "hasSource, hasLegal",
        },
        args.id
            ? {
                  resource: `${args.section}_detail`,
                  args: {
                      projectId: args.projectId,
                      fiscalYear: args.fiscalYear,
                      id: args.id,
                      locale: args.locale || "",
                      ...(viewMode && { viewMode }), // Add viewMode to detail query too
                  },
                  body: `...${args.section}_detail`,
              }
            : {},
    ].filter((r) => !!r.resource);
};

const repository = (path, navigation, dragAndDropActive = false) => ({
    path,
    navigation,
    component: RepositoryController,
    exact: false,
    queries,
    title: "files",
    isOverview: true,
    hasSource: "query(4).hasSource",
    hasLegal: "query(4).hasLegal",
    headerComponentConfig: {
        showButton: true,
        label: "Upload",
        type: "repository",
    },
    headerComponents: [{ component: UploadFormHeaderButtons }],
    components: [
        {
            component: RepositoryTabs,
            parsedName: "RepositoryTabs",
            props: {
                path: path,
                hasSource: "query(4).hasSource",
                hasLegal: "query(4).hasLegal",
            },
        },
        ...buildDatagrid(dragAndDropActive),
    ],
    routes: [
        {
            path: "/:locale(pt|en|lt|es|ro|fr)?/:projectId/:fiscalYear/:importId/repository/:section/:id",
            exact: true,
            queries,
            component: StackLayout,
            components: [
                {
                    component: FileDetail,
                    props: {
                        modules: "query(2)",
                        data: "query(5)",
                    },
                },
            ],
        },
    ],
});

export default repository;
